// THE MAIN-VIEW NARRATIVE — the big-type story of one draft, rendered by
// DraftFlowView's "Main" toggle.
//
// WHAT THIS VIEW IS FOR, and the only thing it is for: giving a skeptical
// reader enough to conclude "this is backed by research, not hallucinated."
// Everything that merely explained the METHOD — word counts, humanize
// counters, the cadence ladder recited, the playbook recited, "how it drafted"
// — has been cut. It taught a reader nothing about THIS lead, and a story that
// pads itself is exactly what a made-up story looks like. What is left is
// evidence:
//
//   1. WHY THIS LEAD          the gate it passed, and the fit read on the row
//   2. FROM THEIR DIRECTORY ROW the imported fields the email wrote from
//   3. RESEARCHED FUN FACT    what OpenAI web search found on the live web for
//                             this company — the one personal line an email
//                             opens with — and links to the pages it read
//
// Every section is EVIDENCE. There is deliberately no closing beat asserting
// that nothing was invented and nothing was sent: a page that has to tell you
// it is honest is doing less work than one that simply shows its sources.
//
// SECTION 3 EXISTS ONLY WHEN THERE IS A RESEARCHED FACT TO SHOW, and it never
// launders a row fact as research. Whether a fact is researched is not inferred:
// the draft's own sources[] records it. Every state without such a fact — never
// researched, researched and empty, dossier too thin — is ONE line at the end of
// section 2, where "the email's personal clause came off the row" belongs. It
// used to be a section in all five states, and the empty ones were three big
// sentences explaining what web search is: machinery, not evidence, and exactly
// the padding a reader auditing an email has to scroll past.
//
// SAME TRUTH RULE AS EVERY OTHER FILE. Every sentence is computed from the
// inputs — the lead row, the draft's own sources[], the meta the run actually
// stamped — or it names itself as a human step. An unknown fact is a sentence
// NOT written, never a vaguer one. A lane the run admits it could not read
// (meta.replyUnchecked / meta.exclUnchecked) is retold as unread, never as
// clean.
//
// PURE DERIVATION, NO REACT. Returns
//   [{ id, kicker, lines: [{ text, tone: 'big'|'dim' }],
//      links?: [{href, host}], linksNote?, sources? }]
// so it can be unit-tested in plain node, and so the component that animates
// it owns none of the words.
import {
  readFacts,
  readResearch,
  readSourceLink,
  followupPersonalizes,
  RESEARCH_LABELS,
} from './outreachDraft.js'
import { formatChicagoDay } from './outreachDraft.js'
import { WAIT_BUSINESS_DAYS, CADENCE_TOUCHES, touchLabel, isEmailTouch, isCallTouch } from './cadence.js'

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`
const big = (text) => ({ text, tone: 'big' })
const dim = (text) => ({ text, tone: 'dim' })

// ── section 1: WHY THIS TOUCH IS DUE — follow-ups and calls ONLY ────────────
//
// Every line below is a number the run actually computed for THIS item: how
// many business days since the last touch, the date of it, whether the inbox
// could be read, where the touch sits on the ladder. That is evidence.
//
// THERE IS NO INITIAL-OUTREACH VERSION, deliberately. It used to open every
// cold draft with "Nobody at <company> had been contacted. That is the gate. A
// company already in a thread never gets a cold intro." plus the trade read off
// the row — and since every cold lead passes the same gate and nearly every row
// in this directory says the same trade, it printed the SAME four lines on
// every card. A section that never varies teaches the reader to skip the top of
// the page, and it pushed the lead's own facts below the fold. The gate itself
// is not lost: the Developer view's outreach-history node states it, per lead,
// with the run's own sentence.

// The follow-up gate, retold from the numbers the run actually stamped on the
// item. A missing meta field silences the sentence that would have carried it.
function whyFollowupGate(meta) {
  const lines = []
  const daysSince = Number.isFinite(meta.daysSince) ? meta.daysSince : null
  const day = formatChicagoDay(meta.lastTouchAt)

  if (daysSince !== null) {
    lines.push(big(`Bo emailed them ${plural(daysSince, 'business day', 'business days')} ago.`))
  } else {
    lines.push(big(`This lead is past the ${WAIT_BUSINESS_DAYS}-business-day wait gate.`))
  }
  if (day) lines.push(dim(`Last touch: ${day}, on the Chicago calendar Bo works. Weekends don’t count.`))

  // "Silence." is true by construction ONLY when the reply freeze ran over a
  // readable inbox. A run that could not read says so instead.
  if (meta.replyUnchecked) {
    lines.push(big('The inbox could not be read.'))
    lines.push(dim('So a reply cannot be ruled out — check the thread by hand before this goes anywhere.'))
  } else {
    lines.push(big('Silence.'))
    lines.push(dim('No real reply after the last touch. A genuine reply pulls a lead out — a human answers, never the machine.'))
  }
  if (meta.exclUnchecked) {
    lines.push(dim('The outcome log could not be read either, so the said-no check was skipped — review before acting.'))
  }
  return lines
}

function whyFollowup(meta) {
  const lines = whyFollowupGate(meta)
  const touch = Number.isFinite(meta.touch) ? meta.touch : null
  if (touch !== null) {
    lines.push(big(`Time for touch ${touch} — ${touchLabel(touch)}.`))
  }
  return lines
}

function whyCall(meta) {
  const lines = whyFollowupGate(meta)
  const touch = Number.isFinite(meta.touch) ? meta.touch : null
  if (touch !== null) {
    // What preceded this call, read straight off the ladder — the same
    // arithmetic buildCallPoints does, from the one file that owns the ladder.
    let emails = 0
    let calls = 0
    for (let n = 1; n < touch; n++) {
      if (isEmailTouch(n)) emails += 1
      else if (isCallTouch(n)) calls += 1
    }
    lines.push(big(`Touch ${touch} of ${CADENCE_TOUCHES.length} is a phone call.`))
    lines.push(
      dim(
        calls > 0
          ? `${plural(emails, 'email', 'emails')} and ${plural(calls, 'call', 'calls')} came before it.`
          : `${plural(emails, 'email', 'emails')} came before it.`,
      ),
    )
  } else {
    lines.push(big('This touch on the ladder is a phone call.'))
  }
  return lines
}

// ── section 3: RESEARCHED FUN FACT ──────────────────────────────────────────
//
// The app's OWN web search, told as its own beat — the twin of the
// `ev-research` node in lib/draftProvenance.js. OpenAI web search reads the live
// web for one specific thing about this company (recent work, a signal, a line
// worth opening on), and that is the sentence the email personalizes with.
//
// The section appears when, and only when, the email really used such a fact:
// the fact itself, what found it and when, and the pages it read. Every other
// state is one line on the row section (noResearchLine).
//
// Whether a fact is researched is not inferred: the draft's own sources[]
// records it — a researched entry carries a RESEARCH_LABELS label, the 'OpenAI
// web research' origin, and the real URLs the research read.
//
// The links are the point. They are the dossier's own sources[] — the pages the
// model actually opened — so a reader can go and check the claim instead of
// trusting it. When the research reported none, the section says exactly that
// rather than showing an internal citation ('Masonry directory import') dressed
// up as a website.
const NO_LINKS_NOTE = 'The research reported no source links, so there is nothing to open here — Bo verifies this one himself.'
// When the dossier named DOMAINS but no page URLs. The domains are still real
// external evidence — the sites the research read — so they are shown, as
// TEXT. They are not turned into links: "https://bizjournals.com/" is a page
// the research never opened, and printing it under "the pages it read" would
// be the invented citation this whole file exists to prevent.
const domainsNote = (domains) =>
  `It read ${domains.join(', ')}, but reported no page URLs, so there is nothing to open here.`

function researchedEntryOf(draft) {
  return (
    (Array.isArray(draft?.sources) ? draft.sources : []).find(
      (s) => s && s.label && s.value && RESEARCH_LABELS.includes(s.label),
    ) || null
  )
}

/**
 * THE EXTERNAL PAGES BEHIND ONE RESEARCHED FACT — the only citations the story
 * and the flow node may show: the URLs stamped on the sources[] entry the email
 * actually wrote from, then the dossier's own parsed source URLs. Deduped by
 * href, re-validated here rather than trusted.
 *
 * WHOLE URLs ONLY, AND NEVER A DOMAIN PROMOTED INTO ONE. research_sources
 * carries bare hostnames the engine extracted FROM those URLs, and it is
 * tempting to link them — but "https://indeed.com/" is a page the research
 * never opened, printed under the words "The pages it read". That is an
 * invented source link, the exact failure this whole rewrite exists to kill,
 * and it also quietly swallows the no-links admission: a dossier that cited no
 * URL would show three fabricated homepages instead of saying it had none.
 * Domains stay display TEXT (the entry's origin line); only research_source_urls
 * becomes an href.
 *
 * [] when the research linked nothing — and [] means the UI says so, never
 * that an internal provenance string ('Masonry directory import') stands in.
 *
 * @returns {{href: string, host: string}[]}
 */
export function researchLinks(entry = null, research = null) {
  const out = []
  const add = (value) => {
    // Re-parsed here, at the last step before an href: http(s) only.
    const link = readSourceLink(value)
    if (link && !out.some((x) => x.href === link.href)) out.push(link)
  }
  for (const u of Array.isArray(entry?.urls) ? entry.urls : []) add(u)
  for (const l of Array.isArray(research?.sourceUrls) ? research.sourceUrls : []) add(l?.href)
  return out.slice(0, 8)
}

/**
 * THE SECTION EXISTS ONLY WHEN THERE IS A RESEARCHED FACT TO SHOW.
 *
 * It used to render in all five states, and the empty ones were three big
 * sentences explaining what web search is ("This is where OpenAI web search
 * reads the live web for one specific thing about Messam Construction…"). That
 * is a description of the machinery, and a reader checking whether an email is
 * hallucinated learns nothing from it — it is the padding this whole view was
 * cut down to remove. Worse, it made the absence of research look like an
 * event.
 *
 * So: a researched fact gets a section, because the fact and its pages are
 * exactly what a skeptical reader needs. Every state without one returns null
 * and is told in ONE line on the row section instead (see noResearchLine) —
 * which is all "this email's personal line came off the row" ever needed.
 */
function researchSection(draft, research) {
  const entry = researchedEntryOf(draft)
  if (!research?.researched || !entry) return null
  const when = formatChicagoDay(research?.at)
  // ORDER MATTERS: the link list renders AFTER the last line, so the sentence
  // that introduces it has to BE the last line. The fact, what found it and
  // when, then the pages — or the admission that it cited none.
  const links = researchLinks(entry, research)
  const lines = [
    big(/[.!?]$/.test(entry.value) ? entry.value : `${entry.value}.`),
    dim(`${entry.label}, found by OpenAI web search on the live web${when ? ` on ${when}` : ''} — and written into the email as its one personal line.`),
  ]
  if (links.length) {
    lines.push(dim(links.length === 1 ? 'The page it read:' : 'The pages it read:'))
  } else {
    const domains = (Array.isArray(research?.sources) ? research.sources : []).filter(Boolean)
    lines.push(dim(domains.length ? domainsNote(domains) : NO_LINKS_NOTE))
  }
  return { id: 'funfact', kicker: 'Researched fun fact', lines, links }
}

/**
 * The one line the row section carries when no researched fact reached the
 * email. One line, not a section: what a reader needs to know is that the
 * email's personal clause came off the import row, and which of the three
 * reasons it did — never a paragraph about what the research step is for.
 *
 * '' when a researched fact WAS used; the section above is then the answer.
 */
function noResearchLine(draft, research, attempted) {
  if (research?.researched) {
    return researchedEntryOf(draft)
      ? ''
      : 'Web research ran on this lead but found nothing that fit one line, so the email opens on the row alone.'
  }
  return attempted
    ? 'Web research ran on this lead and came back with nothing, so the email opens on the row alone.'
    : 'Nothing was researched for this one — the email opens on the row alone, and Bo runs his own web check before it sends.'
}

// ── the call sheet ──────────────────────────────────────────────────────────
//
// A CALL keeps its sheet, because the talking points and the number ARE the
// evidence a rep works from — every point is a fact held or a step Bo takes.
function sheetCall({ meta }) {
  const lines = []
  const points = Array.isArray(meta.points) ? meta.points : []
  const phone = String(meta.phone ?? '').trim()

  if (phone) {
    lines.push(big(`Best number: ${phone}.`))
    lines.push(dim('Read off the lead’s own contact fields — not looked up anywhere.'))
  } else {
    lines.push(big('No phone number is held.'))
    lines.push(dim('The prep says so rather than inventing one. Finding the number is Bo’s step.'))
  }
  for (const p of points) lines.push(dim(String(p)))
  return lines
}

// ── section 2b: WHAT WENT INTO THE EMAIL ────────────────────────────────────
//
// The row fields the draft actually wrote from, named one per line. This is
// NOT the cut "How it drafted" section coming back: that one explained the
// method (the playbook stages, the hook rotation, a word count). This one is
// pure evidence — the exact values a reader can compare against the email in
// front of them, which is the whole test of "is this hallucinated?".
//
// It is deliberately NOT titled with anything resembling "researched": these
// are import fields. When a personalization fact came off the row (service
// area, commercial focus, annual volume — the "LA + Santa Barbara + Ventura,
// CA" case), it is named here, under a heading that says directory row, with
// its import origin told as PROSE. It never becomes a citation, and it never
// sits under the research heading.
function rowFactsLines(company, f, draft, research, attempted) {
  const lines = []
  if (company) lines.push(big(`The company: ${company}.`))
  if (f.location) lines.push(big(`The city: ${f.location}.`))
  if (f.trade) lines.push(big(`The trade: ${f.trade}.`))
  if (f.first) lines.push(big(`A first name: ${f.first}.`))
  if (!lines.length) return []

  // The row-derived personalization the email really used, if any — read off
  // the draft's OWN sources[], the record of what it wrote from, and only when
  // the entry is NOT a researched one (those belong to the research section).
  const rowFact = (Array.isArray(draft?.sources) ? draft.sources : []).find(
    (s) => s && s.label && s.value && !RESEARCH_LABELS.includes(s.label) && ROW_PERSONAL_LABELS.includes(s.label),
  )
  if (rowFact) {
    lines.push(big(`${rowFact.value}.`))
    // The origin is told as PROSE — "a column on their directory row" — and the
    // importer's own name is deliberately NOT printed. Reproducing the literal
    // provenance string ('Masonry directory import') next to a fact is what
    // made it read as a citation in the first place; the honest content of that
    // string is "it came off the row", so that is what the sentence says.
    lines.push(dim(`${rowFact.label} — a column on their directory row, imported, not looked up.`))
  } else {
    lines.push(dim('Every clause in the email is one of those values. An unknown field is a clause not written.'))
  }
  // AND ONE LINE about the research, when there is no researched fact to show.
  // This is where it belongs: the subject is why the email's personal clause
  // came off the row, which is a fact about the row. A whole section for it was
  // three sentences of machinery description for a reader who only needed this.
  const noResearch = noResearchLine(draft, research, attempted)
  if (noResearch) lines.push(dim(noResearch))
  return lines
}

// The row columns that can supply the email's one personalization sentence.
// Kept local and explicit: PERSONALIZATION_LABELS also contains the researched
// labels, and mixing the two is exactly the bug this file exists to prevent.
const ROW_PERSONAL_LABELS = ['Service area', 'Commercial focus', 'Annual volume']

// NO GUARANTEE SECTION. "What you can check" claimed nothing was invented and
// nothing was sent — both true, and both of them ABOUT the page rather than
// evidence on it. The sections above already are the check: a reader compares
// the email in front of them against the row fields and the researched fact
// named here, which is worth more than a paragraph asserting that they match.
// The "nothing was sent" promise itself has not left the screen — DraftFlowView
// renders it as the standing footer chip under every story (KINDS.*.footer),
// which is where a standing promise belongs.

/**
 * Build the main-view narrative for one draft (or call prep).
 *
 * @param {object} args
 * @param {'initial'|'followup'|'call'} [args.kind]
 * @param {object} [args.lead]   the receipt stub — { key, name, email }
 * @param {object|string} [args.facts]  local intel snapshot (object or a bare
 *                                      company string), as draftProvenance takes
 * @param {object} [args.draft]  the HUMANIZED draft — { subject, bodyText, sources }
 * @param {object} [args.meta]   the run's real numbers and admissions —
 *                               { touch, daysSince, lastTouchAt, touchCount,
 *                                 replyUnchecked?, exclUnchecked?,
 *                                 points?, phone? }
 * @param {object[]} [args.passes]  unused today; accepted so the caller can
 *                                  hand over everything it holds
 * @returns {{ id: string, kicker: string, lines: {text: string, tone: 'big'|'dim'}[],
 *             links?: {href: string, host: string}[], sources?: object[] }[]}
 */
export function buildNarrative({ kind = 'initial', lead = {}, facts = null, draft = null, meta = null } = {}) {
  const f = readFacts({
    ...(typeof facts === 'object' && facts ? facts : {}),
    name: lead?.name,
    email: lead?.email,
  })
  // A snapshot stored as a bare company string still counts as a known company
  // — the same tolerance draftProvenance.js extends.
  const company = f.company || (typeof facts === 'string' ? facts.trim() : '') || ''
  const m = meta && typeof meta === 'object' ? meta : {}
  // The research signal, read off whichever holder carries it — the intel
  // snapshot (the engine's merged row) when it does, else the receipt stub's
  // bounded copies. Old receipts carry neither and read { researched: false },
  // which renders NO researched section at all.
  const research = (() => {
    const fromFacts = readResearch(typeof facts === 'object' && facts ? facts : {})
    return fromFacts.researched ? fromFacts : readResearch(lead || {})
  })()

  const isCall = kind === 'call'
  const sections = [
    // Only a follow-up or a call has a "why" worth printing — see above. An
    // initial draft opens on its own row facts instead.
    ...(isCall || kind === 'followup'
      ? [
          {
            id: 'why',
            kicker: isCall ? 'Why this call' : 'Why this lead',
            lines: isCall ? whyCall(m) : whyFollowup(m),
          },
        ]
      : []),
    // The row fields the email was built from — and, when no researched fact
    // reached it, the one line saying so. Never on a call (the sheet IS its
    // evidence), and dropped entirely when the row held nothing but a name — an
    // empty section is not rendered hollow.
    ...(isCall
      ? []
      : [
          {
            id: 'row',
            kicker: 'From their directory row',
            lines: rowFactsLines(company, f, draft, research, Boolean(lead?.researchAttempted)),
          },
        ]),
    // THE RESEARCH, last: the row is what we imported, the fun fact is what we
    // went and looked up, and a reader meets them in that order. It exists ONLY
    // when there is a researched fact to show — researchSection returns null
    // otherwise, and the row section above carries the one line instead.
    ...(isCall ? [] : [researchSection(draft, research)].filter(Boolean)),
    ...(isCall ? [{ id: 'sheet', kicker: 'What’s on the sheet', lines: sheetCall({ meta: m }) }] : []),
  ]

  // NO `sources` KEY, DELIBERATELY. The story used to hang the draft's whole
  // sources[] on its last section, which rendered a chip labelled "Sources"
  // listing 'Masonry directory import' — an internal provenance string dressed
  // as a citation, under a heading that called the fact researched. The only
  // citations this view may show are `links`: external pages a reader can open.
  // Row provenance is told in prose, in the lines above, where it belongs.
  return sections.filter((s) => s.lines.length > 0)
}

export default buildNarrative
